/** Custom web live chat WebSocket frames (part-e API Gateway WS). */

import { environment } from 'src/environments/environment';
import { LIVE_CHAT_SESSION_ENDED_MESSAGE } from './live-chat-user-message';

export interface WebChatAttachmentRef {
  key: string;
  name: string;
  mime?: string;
  mime_type?: string;
  size?: number;
}

export interface WebChatParsedFrame {
  role: string;
  displayName?: string;
  text: string;
  attachments: WebChatAttachmentRef[];
  messageId?: string;
  sessionEnded?: boolean;
}

/** Resolved WebSocket base URL (wss://…/stage). Prefer explicit env; else derive from part-e REST root. */
export function resolveWebChatWsBase(): string {
  const explicit = String(environment.webChatWsUrl || '').trim();
  if (explicit) return explicit.replace(/\/$/, '');
  const root = String(environment.partEApiRoot || '').trim();
  if (!root) return '';
  return root.replace(/^https:\/\//i, 'wss://').replace(/\/$/, '');
}

function normalizeAttachments(raw: unknown): WebChatAttachmentRef[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((a) => ({
      key: String(a?.key ?? a?.object_key ?? ''),
      name: String(a?.name ?? a?.file_name ?? 'Attachment'),
      mime: String(a?.mime ?? a?.mime_type ?? ''),
      size: a?.size != null ? Number(a.size) : undefined,
    }))
    .filter((x) => x.key);
}

function senderTypeToRole(senderType: string | undefined): string {
  const t = String(senderType || '').toLowerCase();
  if (t === 'customer') return 'CUSTOMER';
  if (t === 'agent') return 'AGENT';
  if (t === 'system') return 'SYSTEM';
  return String(senderType || 'UNKNOWN').toUpperCase();
}

export function buildWebSocketUrl(wsUrl: string, wsToken: string): string {
  const base = (wsUrl || resolveWebChatWsBase()).replace(/\/$/, '');
  if (!base || !wsToken) return '';
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}token=${encodeURIComponent(wsToken)}`;
}

export function parseWebLiveChatWebSocketFrame(raw: string): WebChatParsedFrame | null {
  try {
    const data = JSON.parse(raw);
    const type = String(data?.type || '');

    if (type === 'session.ended') {
      return {
        role: 'SYSTEM',
        text: LIVE_CHAT_SESSION_ENDED_MESSAGE,
        attachments: [],
        sessionEnded: true,
      };
    }

    if (type === 'session.active') {
      const agentName = String(data?.agent_name || '').trim();
      return {
        role: 'SYSTEM',
        displayName: agentName || undefined,
        text: agentName ? `Agent ${agentName} joined the chat.` : 'An agent joined the chat.',
        attachments: [],
      };
    }

    if (type === 'message.new') {
      const msg = data?.message;
      if (!msg || typeof msg !== 'object') return null;
      const role = senderTypeToRole(msg.sender_type);
      const text = String(msg.content || '').trim();
      const attachments = normalizeAttachments(msg.attachments);
      if (!text && attachments.length === 0) return null;
      return {
        role,
        displayName: msg.sender_name ? String(msg.sender_name) : undefined,
        text,
        attachments,
        messageId: msg.message_id ? String(msg.message_id) : undefined,
      };
    }

    return null;
  } catch {
    return null;
  }
}

export async function websocketMessageDataAsString(ev: MessageEvent): Promise<string> {
  const d = ev.data;
  if (typeof d === 'string') return d;
  if (d instanceof Blob) return d.text();
  if (d instanceof ArrayBuffer) return new TextDecoder().decode(d);
  return String(d);
}
